/**
 * Versions (work packages) slice (PX-W6.3, TASK-EDD04F2F).
 *
 * Cache keyed by the project slug; every mutation refetches on fulfilment
 * (the activate chain is server-validated — an optimistic write would be a
 * second source of truth for "which version is ACTIVE"). Bus `version.*`
 * frames invalidate the loaded cache when they concern it, bumping a nonce
 * the mounted panel refetches on (refetch-on-invalidate, payload ids only —
 * the frame's version row is never delta-merged).
 */

import { createAction, createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit'

import { isApiError, toApiError } from '@/services'
import {
  fetchFailed,
  fetchStarted,
  fetchSucceeded,
  initialFetchCache,
  invalidate,
  markServerChanged,
  shouldFetch,
  type FetchCache
} from '@/store/fetchCache'
import { activeVersionOf } from '../lib/activeVersion'
import { versionService } from '../services/versionService.http'
import type { CreateVersionInput, VersionRow, VersionsBusFrame } from '../types'

export interface VersionsError {
  /** The real HTTP status (c1-2) — lets a consumer route 403/404 vs. any other failure without fabricating one. */
  status: number
  key?: string
  message: string
}

export interface VersionsState extends FetchCache {
  items: VersionRow[]
  /** Bumped by bus frames + local mutations' refetches — the panel's signal. */
  invalidationNonce: number
  /** A mutation in flight (one at a time — the chains are server-ordered). */
  mutating: boolean
  mutationError: VersionsError | null
  /** The LIST fetch failure (drives the panel's error card + Retry). */
  listError: VersionsError | null
  /** The project `listError` was recorded for — never render it for a different one (TASK-63685491, F2b). */
  listErrorKey: string | null
}

const initialState: VersionsState = {
  items: [],
  invalidationNonce: 0,
  mutating: false,
  mutationError: null,
  listError: null,
  listErrorKey: null,
  ...initialFetchCache
}

interface ProjectArg {
  orgId: string
  projectId: string
}

const toVersionsError = (error: unknown): VersionsError => {
  const apiError = isApiError(error) ? error : toApiError(error)
  return { status: apiError.status, key: apiError.backendKey, message: apiError.message }
}

// ---------------------------------------------------------------------------
// Thunks
// ---------------------------------------------------------------------------

export const fetchVersions = createAsyncThunk<
  VersionRow[],
  ProjectArg,
  { rejectValue: VersionsError }
>('projectVersions/fetch', async ({ orgId, projectId }, { rejectWithValue }) => {
  try {
    return await versionService.list(orgId, projectId)
  } catch (error) {
    return rejectWithValue(toVersionsError(error))
  }
}, {
  condition: ({ projectId }, { getState }) => {
    const { projectVersions } = getState() as { projectVersions: VersionsState }
    return shouldFetch(projectVersions, projectId)
  }
})

/** Shared shape for the mutations: act, invalidate, then refetch the list. */
const mutatingThunk = <T extends object>(
  type: string,
  act: (args: T & ProjectArg) => Promise<unknown>
) =>
  createAsyncThunk<void, T & ProjectArg, { rejectValue: VersionsError }>(
    type,
    async (args, { dispatch, rejectWithValue }) => {
      try {
        await act(args)
        // The mutation is not optimistic: re-arm the cache so the refetch
        // below passes the fetch condition (a warm cache would skip it).
        dispatch(versionsRetryArmed())
        await dispatch(fetchVersions({ orgId: args.orgId, projectId: args.projectId }))
      } catch (error) {
        return rejectWithValue(toVersionsError(error))
      }
    }
  )

export const createVersion = mutatingThunk<{ input: CreateVersionInput }>(
  'projectVersions/create',
  (args) => versionService.create(args.orgId, args.projectId, args.input)
)

export const activateVersion = mutatingThunk<{ versionId: string }>(
  'projectVersions/activate',
  (args) => versionService.activate(args.orgId, args.projectId, args.versionId)
)

export const archiveVersion = mutatingThunk<{ versionId: string }>(
  'projectVersions/archive',
  (args) => versionService.archive(args.orgId, args.projectId, args.versionId)
)

export const deleteVersion = mutatingThunk<{ versionId: string }>(
  'projectVersions/delete',
  (args) => versionService.remove(args.orgId, args.projectId, args.versionId)
)

// ---------------------------------------------------------------------------
// Bus frame alias — same type as the realtime nonce, payload-carrying
// ---------------------------------------------------------------------------

export const versionsBusFrame = createAction<VersionsBusFrame>('realtime/invalidateVersions')

// ---------------------------------------------------------------------------
// Slice
// ---------------------------------------------------------------------------

export const versionsSlice = createSlice({
  name: 'projectVersions',
  initialState,
  reducers: {
    /** Leave the workspace — resets everything. */
    versionsClosed(state) {
      state.items = []
      state.mutationError = null
      state.listError = null
      state.listErrorKey = null
      invalidate(state)
    },
    /** Dismiss a refusal (rendered inline by the panel). */
    versionsErrorDismissed(state) {
      state.mutationError = null
      state.listError = null
      state.listErrorKey = null
    },
    /**
     * Re-arm the cache after a list failure (the Retry path) or before the
     * post-mutation reload — a server change, so a mid-flight re-arm survives.
     */
    versionsRetryArmed(state) {
      state.listError = null
      state.listErrorKey = null
      markServerChanged(state)
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchVersions.pending, (state, action) => {
        fetchStarted(state, action.meta.arg.projectId)
        state.listError = null
        state.listErrorKey = null
      })
      .addCase(fetchVersions.fulfilled, (state, action) => {
        // Key-scoped (TASK-3D6DB8B6): a superseded project's reply writes nothing.
        if (!fetchSucceeded(state, action.meta.arg.projectId)) return
        state.items = action.payload
      })
      .addCase(fetchVersions.rejected, (state, action) => {
        if (!fetchFailed(state, action.meta.arg.projectId)) return
        state.listError = action.payload ?? null
        state.listErrorKey = action.meta.arg.projectId
      })
      // The four mutations share pending/fulfilled/rejected shapes.
      for (const thunk of [createVersion, activateVersion, archiveVersion, deleteVersion]) {
        builder
          .addCase(thunk.pending, (state) => {
            state.mutating = true
            state.mutationError = null
          })
          .addCase(thunk.fulfilled, (state) => {
            state.mutating = false
          })
          .addCase(thunk.rejected, (state, action) => {
            state.mutating = false
            state.mutationError = action.payload ?? null
          })
      }
      builder.addCase(versionsBusFrame, (state, action) => {
        const { projectId } = action.payload
        // A frame naming another project (by slug, `version.deleted` only) or
        // a frame with no slug (row envelopes carry the ObjectId hex, which
        // the client cannot compare) invalidates only when nothing narrower
        // is known — the refetch is idempotent, so over-invalidation is safe
        // and under-invalidation is not. A frame for the project whose FIRST
        // load is in flight counts too; mid-flight it earns one follow-up load.
        if (
          projectId === undefined ||
          projectId === state.loadedKey ||
          (state.loading && projectId === state.loadingKey)
        ) {
          state.invalidationNonce += 1
          markServerChanged(state)
        }
      })
  }
})

export const { versionsClosed, versionsErrorDismissed, versionsRetryArmed } =
  versionsSlice.actions

/* ── Selectors ──────────────────────────────────────────────────────────── */

interface WithVersions {
  projectVersions: VersionsState
}

export const selectVersions = (s: WithVersions) => s.projectVersions.items
export const selectVersionsLoading = (s: WithVersions) => s.projectVersions.loading
export const selectVersionsNonce = (s: WithVersions) => s.projectVersions.invalidationNonce
export const selectVersionsMutating = (s: WithVersions) => s.projectVersions.mutating
export const selectVersionsError = (s: WithVersions) => s.projectVersions.mutationError
export const selectVersionsListError = (s: WithVersions) => s.projectVersions.listError
/** The project key the versions cache is currently loaded for (AC-9 — gates a dependent fetch on it). */
export const selectVersionsLoadedKey = (s: WithVersions) => s.projectVersions.loadedKey
/** Module-level empty list, so {@link selectVersionsFor} never returns a fresh array. */
const NO_VERSIONS: readonly VersionRow[] = []
/**
 * The versions of `projectId` ONLY — `items` when the cache is loaded for that
 * project, otherwise an empty list (TASK-3D6DB8B6 fix cycle 1). The slice keeps
 * the PREVIOUS project's rows while the next project's read is pending or after
 * it failed, so `selectVersions` alone cannot say whose rows they are; a
 * consumer that offers a version to act on (a filter chip, a bulk-import
 * target) must read this instead.
 */
export const selectVersionsFor = (s: WithVersions, projectId: string): readonly VersionRow[] =>
  s.projectVersions.loadedKey === projectId ? s.projectVersions.items : NO_VERSIONS
/** `listError` scoped to `projectId` — `null` for any other (or undefined) project, so a stale error never paints on screen for a project it was not recorded for (TASK-63685491, F2b). */
export const selectVersionsListErrorFor = (s: WithVersions, projectId: string | undefined) =>
  s.projectVersions.listErrorKey === projectId ? s.projectVersions.listError : null

/**
 * The version a version-scoped action targets for `projectId` — `activeVersionOf`
 * over THIS project's rows only (`selectVersionsFor`), `null` until they load or
 * when there are none. Memoised on those rows. Added by TASK-41CE1DE2 fix cycle 1
 * (review low: `GenerateTasksPanel` derived it with a `useMemo` over store data).
 */
export const selectActiveVersionFor = createSelector([selectVersionsFor], (rows): VersionRow | null =>
  activeVersionOf(rows)
)
